/**
 * LabPromoBannerCarousel — swipeable / auto-rotating banner strip.
 *
 * Used on:
 *   • /l/{slug}/book                — Public lab booking page
 *   • /l/{slug}/r/{token}           — Public Smart Report share view
 *
 * Props:
 *   banners: [{ id, image_url, link_url?, title? }]
 *   labId:   parent lab id — required to fire impression/click pings
 *   className: optional wrapper classes
 *   height:   Tailwind height utility (default "h-40 sm:h-48")
 */
import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

// Track (lab_id + banner_id) impressions once per browser session so we don't
// double-count auto-rotations or route re-renders.
const IMPRESSION_KEY = "efgh_banner_impressions";
function markImpressed(labId, bannerId) {
  try {
    const raw = sessionStorage.getItem(IMPRESSION_KEY) || "{}";
    const seen = JSON.parse(raw);
    const key = `${labId}::${bannerId}`;
    if (seen[key]) return false;
    seen[key] = Date.now();
    sessionStorage.setItem(IMPRESSION_KEY, JSON.stringify(seen));
    return true;
  } catch {
    return true;
  }
}

async function ping(path) {
  try {
    await api.post(path);
  } catch {
    /* analytics failures are non-fatal */
  }
}

export default function LabPromoBannerCarousel({ banners = [], labId, className = "", height = "h-36 sm:h-40 md:h-44 lg:h-48" }) {
  const [idx, setIdx] = useState(0);
  const n = banners.length;
  const containerRef = useRef(null);
  const [visible, setVisible] = useState(false);

  // Only start firing impressions when the carousel is on-screen.
  useEffect(() => {
    if (!containerRef.current || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setVisible(true)),
      { threshold: 0.25 },
    );
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // Fire an impression when the current banner becomes visible.
  useEffect(() => {
    if (!visible || !labId || n === 0) return;
    const b = banners[idx];
    if (!b?.id || b.id === "legacy") return;
    if (markImpressed(labId, b.id)) {
      ping(`/public/labs/${labId}/banners/${b.id}/impression`);
    }
  }, [visible, idx, labId, banners, n]);

  useEffect(() => {
    if (n < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % n), 5000);
    return () => clearInterval(t);
  }, [n]);

  if (n === 0) return null;
  const b = banners[idx];

  const handleClick = () => {
    if (!labId || !b?.id || b.id === "legacy") return;
    ping(`/public/labs/${labId}/banners/${b.id}/click`);
  };

  const inner = (
    <div className="relative w-full h-full">
      <img
        src={b.image_url}
        alt={b.title || "Promotion"}
        className="w-full h-full object-cover"
        data-testid={`lab-promo-banner-${idx}`}
      />
      {b.title && (
        <div className="absolute bottom-2 left-3 text-white font-heading font-semibold text-sm sm:text-base drop-shadow" data-testid={`lab-promo-title-${idx}`}>
          {b.title}
        </div>
      )}
    </div>
  );

  return (
    <div ref={containerRef} className={`relative rounded-2xl overflow-hidden bg-muted ${height} ${className}`} data-testid="lab-promo-carousel">
      {b.link_url
        ? <a href={b.link_url} target="_blank" rel="noopener noreferrer" onClick={handleClick} className="block h-full">{inner}</a>
        : <button type="button" onClick={handleClick} className="block w-full h-full text-left">{inner}</button>}
      {n > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous banner"
            data-testid="lab-promo-prev"
            onClick={() => setIdx((i) => (i - 1 + n) % n)}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 text-white grid place-items-center transition-all"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            aria-label="Next banner"
            data-testid="lab-promo-next"
            onClick={() => setIdx((i) => (i + 1) % n)}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 text-white grid place-items-center transition-all"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="absolute bottom-2 right-3 flex gap-1.5" data-testid="lab-promo-dots">
            {banners.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Show banner ${i + 1}`}
                className={`w-2 h-2 rounded-full transition-all ${i === idx ? "bg-white w-6" : "bg-white/50"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
